/**
 * Bulk Job Operations Hooks
 *
 * Coordinate multiple use cases at the APPLICATION LAYER.
 *
 * DDD Note: Bulk operations are NOT domain logic - they're application
 * layer coordination. Each job goes through its proper use case individually,
 * ensuring domain validation and business rules are applied.
 *
 * We use client-side iteration instead of a backend bulk endpoint because:
 * - Each Job is its own aggregate root (consistency boundary)
 * - Reuses existing use cases (no duplication)
 * - Proper error handling per job
 * - Transactional integrity maintained
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStartJob, useCompleteJob, useCancelJob, jobKeys } from './useJobData';
import { toast } from 'sonner';

export interface BulkOperationResult {
  succeeded: number;
  failed: number;
  total: number;
  errors?: Array<{ jobId: string; error: string }>;
}

/**
 * Bulk start jobs
 *
 * Iterates over job IDs and calls StartJobUseCase for each.
 * Shows progress and handles partial failures gracefully.
 */
export function useBulkStartJobs() {
  const queryClient = useQueryClient();
  const startJob = useStartJob();

  return useMutation({
    mutationFn: async (jobIds: string[]): Promise<BulkOperationResult> => {
      const results = await Promise.allSettled(
        jobIds.map(id => startJob.mutateAsync(id))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      const errors = results
        .map((r, idx) => {
          if (r.status === 'rejected') {
            return {
              jobId: jobIds[idx],
              error: r.reason?.message || 'Unknown error',
            };
          }
          return null;
        })
        .filter((e): e is { jobId: string; error: string } => e !== null);

      return { succeeded, failed, total: jobIds.length, errors };
    },
    onSuccess: (result) => {
      // Invalidate all job queries to refresh data
      queryClient.invalidateQueries({ queryKey: jobKeys.all });

      // Show success toast
      if (result.failed === 0) {
        toast.success(`Started ${result.succeeded} jobs successfully`);
      } else {
        toast.warning(
          `Started ${result.succeeded} of ${result.total} jobs (${result.failed} failed)`
        );
      }
    },
    onError: (error) => {
      toast.error(`Bulk start operation failed: ${error.message}`);
    },
  });
}

/**
 * Bulk complete jobs
 *
 * Iterates over job IDs and calls CompleteJobUseCase for each.
 */
export function useBulkCompleteJobs() {
  const queryClient = useQueryClient();
  const completeJob = useCompleteJob();

  return useMutation({
    mutationFn: async (jobIds: string[]): Promise<BulkOperationResult> => {
      const results = await Promise.allSettled(
        jobIds.map(id => completeJob.mutateAsync({ jobId: id }))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      const errors = results
        .map((r, idx) => {
          if (r.status === 'rejected') {
            return {
              jobId: jobIds[idx],
              error: r.reason?.message || 'Unknown error',
            };
          }
          return null;
        })
        .filter((e): e is { jobId: string; error: string } => e !== null);

      return { succeeded, failed, total: jobIds.length, errors };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });

      if (result.failed === 0) {
        toast.success(`Completed ${result.succeeded} jobs successfully`);
      } else {
        toast.warning(
          `Completed ${result.succeeded} of ${result.total} jobs (${result.failed} failed)`
        );
      }
    },
    onError: (error) => {
      toast.error(`Bulk complete operation failed: ${error.message}`);
    },
  });
}

/**
 * Bulk cancel jobs
 *
 * Iterates over job IDs and calls CancelJobUseCase for each.
 * Requires a cancellation reason that applies to all jobs.
 */
export function useBulkCancelJobs() {
  const queryClient = useQueryClient();
  const cancelJob = useCancelJob();

  return useMutation({
    mutationFn: async ({
      jobIds,
      reason,
    }: {
      jobIds: string[];
      reason: string;
    }): Promise<BulkOperationResult> => {
      const results = await Promise.allSettled(
        jobIds.map(id =>
          cancelJob.mutateAsync({ jobId: id, reason })
        )
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      const errors = results
        .map((r, idx) => {
          if (r.status === 'rejected') {
            return {
              jobId: jobIds[idx],
              error: r.reason?.message || 'Unknown error',
            };
          }
          return null;
        })
        .filter((e): e is { jobId: string; error: string } => e !== null);

      return { succeeded, failed, total: jobIds.length, errors };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });

      if (result.failed === 0) {
        toast.success(`Cancelled ${result.succeeded} jobs successfully`);
      } else {
        toast.warning(
          `Cancelled ${result.succeeded} of ${result.total} jobs (${result.failed} failed)`
        );
      }
    },
    onError: (error) => {
      toast.error(`Bulk cancel operation failed: ${error.message}`);
    },
  });
}
